import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  MessageCircle, Send, Mic, MicOff, Volume2, VolumeX,
  Check, CheckCheck, Search, Megaphone, Trash2, ArrowLeft, User as UserIcon
} from 'lucide-react';
import { toast } from 'sonner';

const TTS_ENABLED_KEY = 'propilote_tts_enabled';
const POLL_INTERVAL_MS = 20000;   // 20s (reduit de 8s pour eviter surcharge Android)

/**
 * MessagesPage — Style WhatsApp
 * - Admin/Dispatch : liste des conversations a gauche + chat a droite (desktop)
 *                    ou navigation mobile (liste OU chat)
 * - Chauffeur : chat direct avec le dispatch (une seule conversation)
 * - Bonus : dictee vocale (Web Speech API) + TTS auto-read messages entrants
 * - Broadcast : admin peut envoyer a "tous les chauffeurs" en fanout personnalise
 */
const MessagesPage = () => {
  const { user } = useAuth();
  const { get, post, del } = useApi();

  const isAdmin = ['admin', 'super_admin', 'dispatcher', 'dispatch'].includes(user?.role);

  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);   // { conversation_id, other_user_id, other_user_name, ... }
  const [messages, setMessages] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastText, setBroadcastText] = useState('');
  const [ttsEnabled, setTtsEnabled] = useState(() => localStorage.getItem(TTS_ENABLED_KEY) === 'true');
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef(null);
  const messagesEndRef = useRef(null);
  const lastReadMessageIdRef = useRef(null);

  // Init reconnaissance vocale (SpeechRecognition)
  const dictationBaseRef = useRef('');   // Texte deja tape avant demarrage dictee
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.continuous = true;         // permet phrases plus longues sans coupure
    rec.interimResults = true;     // affichage temps reel du texte partiel
    rec.lang = 'fr-CA';
    rec.onresult = (ev) => {
      // ev.results contient TOUS les resultats depuis le start.
      // On separe le final (commit) de l'interim (temporaire) pour eviter les doublons.
      let finalText = '';
      let interimText = '';
      for (let i = 0; i < ev.results.length; i++) {
        const res = ev.results[i];
        if (res.isFinal) finalText += res[0].transcript;
        else interimText += res[0].transcript;
      }
      const base = dictationBaseRef.current || '';
      const separator = base && !base.endsWith(' ') ? ' ' : '';
      const merged = (base + separator + finalText + interimText).replace(/\s+/g, ' ').trimStart();
      setNewMessage(merged);
    };
    rec.onerror = (ev) => {
      console.warn('[SpeechRecognition] error:', ev.error);
      setIsRecording(false);
    };
    rec.onend = () => setIsRecording(false);
    recognitionRef.current = rec;
    return () => { try { rec.stop(); } catch (e) {} };
  }, []);

  // TTS : lit les messages entrants a voix haute (si toggle actif)
  const speak = useCallback((text) => {
    if (!ttsEnabled || !text) return;
    if (!window.speechSynthesis) return;
    try {
      window.speechSynthesis.cancel();   // annule si en cours
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = 'fr-CA';
      utter.rate = 1.0;
      utter.pitch = 1.0;
      window.speechSynthesis.speak(utter);
    } catch (e) { console.warn('[TTS] error:', e); }
  }, [ttsEnabled]);

  const toggleTts = () => {
    const next = !ttsEnabled;
    setTtsEnabled(next);
    localStorage.setItem(TTS_ENABLED_KEY, String(next));
    if (!next && window.speechSynthesis) window.speechSynthesis.cancel();
    toast.success(next ? 'Lecture vocale activee' : 'Lecture vocale desactivee');
  };

  const toggleRecording = () => {
    const rec = recognitionRef.current;
    if (!rec) {
      toast.error('Dictee vocale non supportee sur cet appareil');
      return;
    }
    if (isRecording) {
      try { rec.stop(); } catch (e) {}
      setIsRecording(false);
    } else {
      try {
        // Sauvegarde le texte deja tape pour eviter que la dictee ecrase / duplique
        dictationBaseRef.current = newMessage || '';
        rec.start();
        setIsRecording(true);
      } catch (e) {
        console.warn('[SR] start failed:', e);
        toast.error('Impossible de demarrer la dictee');
      }
    }
  };

  // Charge la liste des conversations
  const fetchConversations = useCallback(async () => {
    const result = await get('/messages/conversations');
    if (result.success) {
      const convs = result.data?.conversations || [];
      setConversations(convs);
      // Auto-selection : si chauffeur (une seule conv), on l'ouvre direct
      if (!isAdmin && convs.length > 0 && !selectedConv) {
        setSelectedConv(convs[0]);
      }
      // Si chauffeur sans conversation existante, on prepare une conv vide vers l'admin dispatch
      if (!isAdmin && convs.length === 0 && !selectedConv) {
        setSelectedConv({ other_user_id: null, other_user_name: 'Dispatch', other_user_role: 'admin', conversation_id: null, message_count: 0, unread_count: 0 });
      }
    }
    setLoadingConvs(false);
  }, [get, isAdmin, selectedConv]);

  // Charge les messages d'une conversation
  const fetchMessages = useCallback(async (otherUserId) => {
    if (!otherUserId) { setMessages([]); return; }
    setLoadingMsgs(true);
    const result = await get(`/messages/conversations/${otherUserId}`);
    if (result.success) {
      const msgs = result.data?.messages || [];
      // Detection nouveaux messages recus pour TTS
      const lastRecvId = [...msgs].reverse().find(m => m.sender_id !== user?.id)?.id;
      if (ttsEnabled && lastRecvId && lastRecvId !== lastReadMessageIdRef.current) {
        const lastRecv = msgs.find(m => m.id === lastRecvId);
        if (lastRecv) speak(`Nouveau message de ${lastRecv.sender_name}. ${lastRecv.content}`);
        lastReadMessageIdRef.current = lastRecvId;
      }
      setMessages(msgs);
    }
    setLoadingMsgs(false);
  }, [get, user?.id, ttsEnabled, speak]);

  // Initial load + polling (skip si onglet cache pour economiser CPU/batterie Android)
  useEffect(() => {
    fetchConversations();
    const iv = setInterval(() => {
      if (document.visibilityState === 'visible') fetchConversations();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [fetchConversations]);

  useEffect(() => {
    if (!selectedConv?.other_user_id) return;
    fetchMessages(selectedConv.other_user_id);
    const iv = setInterval(() => {
      if (document.visibilityState === 'visible') fetchMessages(selectedConv.other_user_id);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [selectedConv?.other_user_id, fetchMessages]);

  // Auto-scroll bas
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Envoi message 1-a-1
  const handleSend = async (e) => {
    e?.preventDefault?.();
    if (!newMessage.trim() || sending) return;
    if (!selectedConv) { toast.error('Selectionnez une conversation'); return; }

    setSending(true);
    const content = newMessage.trim();
    setNewMessage('');
    // Stop dictee si active
    if (isRecording) { try { recognitionRef.current?.stop(); } catch (e) {} setIsRecording(false); }

    // Ajout optimiste
    const tempMsg = {
      id: `tmp-${Date.now()}`,
      sender_id: user?.id,
      sender_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Moi',
      sender_role: user?.role,
      recipient_id: selectedConv.other_user_id,
      content,
      type: 'text',
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setMessages(prev => [...prev, tempMsg]);

    const result = await post('/messages/send', {
      recipient_id: selectedConv.other_user_id || undefined,
      content,
    });

    if (result.success) {
      // Refetch pour avoir le vrai id + confirmer
      await fetchMessages(selectedConv.other_user_id);
      await fetchConversations();
    } else {
      toast.error('Impossible d\'envoyer le message');
      setMessages(prev => prev.filter(m => m.id !== tempMsg.id));
      setNewMessage(content);
    }
    setSending(false);
  };

  // Envoi broadcast (admin only)
  const handleBroadcast = async () => {
    if (!broadcastText.trim()) return;
    setSending(true);
    const result = await post('/messages/send', {
      broadcast_scope: 'all_drivers',
      content: broadcastText.trim(),
    });
    setSending(false);
    if (result.success) {
      toast.success('Message envoye a tous les chauffeurs actifs');
      setBroadcastText('');
      setShowBroadcastModal(false);
      fetchConversations();
    } else {
      toast.error('Erreur envoi broadcast');
    }
  };

  // Suppression conversation
  const handleDeleteConv = async () => {
    if (!selectedConv?.other_user_id) return;
    if (!window.confirm(`Supprimer la conversation avec ${selectedConv.other_user_name} ? Cette action est reversible cote serveur uniquement.`)) return;
    const result = await del(`/messages/conversations/${selectedConv.other_user_id}`);
    if (result.success) {
      toast.success('Conversation supprimee');
      setSelectedConv(null);
      setMessages([]);
      fetchConversations();
    } else {
      toast.error('Erreur suppression');
    }
  };

  const filteredConvs = conversations.filter(c =>
    !searchQuery || (c.other_user_name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' });
    }
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Hier';
    return d.toLocaleDateString('fr-CA', { day: 'numeric', month: 'short' });
  };

  // ========== RENDER ==========

  // Vue mobile (chauffeur) : chat plein ecran uniquement
  if (!isAdmin) {
    return (
      <div className="page-wrapper flex flex-col h-screen bg-slate-950" data-testid="messages-page-driver">
        {/* Header */}
        <div className="flex-shrink-0 bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center gap-3">
          <MessageCircle className="text-amber-500" size={24} />
          <div className="flex-1">
            <h1 className="text-lg font-bold text-white">Dispatch</h1>
            <p className="text-xs text-slate-400">Communication avec votre repartiteur</p>
          </div>
          <button
            onClick={toggleTts}
            className={`p-2 rounded-lg ${ttsEnabled ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-400'}`}
            title={ttsEnabled ? 'Desactiver la lecture vocale' : 'Activer la lecture vocale (mains libres)'}
            data-testid="toggle-tts"
          >
            {ttsEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
          </button>
        </div>

        {/* Messages */}
        <ChatMessages
          messages={messages}
          userId={user?.id}
          loading={loadingMsgs}
          messagesEndRef={messagesEndRef}
          formatTime={formatTime}
        />

        {/* Input */}
        <MessageInput
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          onSend={handleSend}
          sending={sending}
          isRecording={isRecording}
          toggleRecording={toggleRecording}
        />
      </div>
    );
  }

  // Vue desktop (admin) : split-screen conversations + chat
  return (
    <div className="page-wrapper flex h-[calc(100vh-4rem)] bg-slate-950" data-testid="messages-page-admin">
      {/* Colonne gauche : liste conversations */}
      <div className={`${selectedConv ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 lg:w-96 border-r border-slate-800 bg-slate-900`}>
        <div className="p-4 border-b border-slate-800 flex items-center gap-2">
          <MessageCircle className="text-amber-500" size={22} />
          <h2 className="text-white font-bold flex-1">Messages</h2>
          <button
            onClick={() => setShowBroadcastModal(true)}
            className="p-2 bg-amber-500 hover:bg-amber-400 text-black rounded-lg"
            title="Envoyer a tous les chauffeurs actifs"
            data-testid="btn-open-broadcast"
          >
            <Megaphone size={18} />
          </button>
        </div>
        <div className="p-3 border-b border-slate-800">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Rechercher un chauffeur..."
              className="pl-9 bg-slate-800 border-slate-700 text-white"
              data-testid="input-search-conv"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="conversations-list">
          {loadingConvs && <div className="p-4 text-slate-500 text-sm">Chargement...</div>}
          {!loadingConvs && filteredConvs.length === 0 && (
            <div className="p-6 text-center text-slate-500 text-sm">
              Aucune conversation.<br />
              Envoyez un broadcast pour demarrer.
            </div>
          )}
          {filteredConvs.map(conv => {
            const active = selectedConv?.conversation_id === conv.conversation_id;
            return (
              <button
                key={conv.conversation_id}
                onClick={() => setSelectedConv(conv)}
                className={`w-full text-left px-4 py-3 border-b border-slate-800 hover:bg-slate-800 transition ${active ? 'bg-slate-800' : ''}`}
                data-testid={`conv-${conv.other_user_id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 bg-amber-500/20 text-amber-500 rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                    {conv.other_user_name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-baseline gap-2">
                      <span className="text-white font-medium truncate">{conv.other_user_name}</span>
                      <span className="text-xs text-slate-500 flex-shrink-0">{formatTime(conv.last_message?.created_at)}</span>
                    </div>
                    <div className="flex justify-between items-center gap-2 mt-1">
                      <p className="text-sm text-slate-400 truncate">
                        {conv.last_message?.sender_id === user?.id && <span className="text-slate-500">Vous: </span>}
                        {conv.last_message?.content}
                      </p>
                      {conv.unread_count > 0 && (
                        <span className="bg-amber-500 text-black text-xs font-bold rounded-full px-2 py-0.5 flex-shrink-0" data-testid={`unread-badge-${conv.other_user_id}`}>
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Colonne droite : chat */}
      <div className={`${selectedConv ? 'flex' : 'hidden md:flex'} flex-col flex-1 bg-slate-950`}>
        {selectedConv ? (
          <>
            <div className="flex-shrink-0 bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center gap-3">
              <button
                className="md:hidden p-1 hover:bg-slate-800 rounded"
                onClick={() => setSelectedConv(null)}
              >
                <ArrowLeft size={20} className="text-white" />
              </button>
              <div className="w-10 h-10 bg-amber-500/20 text-amber-500 rounded-full flex items-center justify-center font-bold">
                {selectedConv.other_user_name?.charAt(0).toUpperCase() || '?'}
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">{selectedConv.other_user_name}</p>
                <p className="text-xs text-slate-400">
                  {selectedConv.other_user_role === 'driver' ? 'Chauffeur' :
                    selectedConv.other_user_role === 'mechanic' ? 'Mecanicien' : 'Utilisateur'}
                </p>
              </div>
              <button
                onClick={toggleTts}
                className={`p-2 rounded-lg ${ttsEnabled ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-400'}`}
                title={ttsEnabled ? 'Desactiver TTS' : 'Activer TTS (lecture vocale des messages entrants)'}
                data-testid="toggle-tts-admin"
              >
                {ttsEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
              </button>
              <button
                onClick={handleDeleteConv}
                className="p-2 bg-slate-800 hover:bg-red-500/20 hover:text-red-400 text-slate-400 rounded-lg"
                title="Supprimer la conversation"
                data-testid="btn-delete-conv"
              >
                <Trash2 size={18} />
              </button>
            </div>

            <ChatMessages
              messages={messages}
              userId={user?.id}
              loading={loadingMsgs}
              messagesEndRef={messagesEndRef}
              formatTime={formatTime}
            />

            <MessageInput
              newMessage={newMessage}
              setNewMessage={setNewMessage}
              onSend={handleSend}
              sending={sending}
              isRecording={isRecording}
              toggleRecording={toggleRecording}
            />
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-500">
            <div className="text-center">
              <MessageCircle className="mx-auto mb-4 text-slate-700" size={64} />
              <p>Selectionnez une conversation</p>
              <p className="text-sm text-slate-600 mt-1">ou envoyez un broadcast a tous les chauffeurs</p>
            </div>
          </div>
        )}
      </div>

      {/* Modal Broadcast */}
      {showBroadcastModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-500 text-black rounded-full flex items-center justify-center">
                <Megaphone size={20} />
              </div>
              <h3 className="text-white text-lg font-bold">Message a tous les chauffeurs</h3>
            </div>
            <p className="text-sm text-slate-400 mb-3">
              Ce message sera envoye en prive a chaque chauffeur actif. Ils pourront repondre individuellement (les autres chauffeurs ne verront pas les reponses).
            </p>
            <textarea
              value={broadcastText}
              onChange={e => setBroadcastText(e.target.value)}
              placeholder="Ex: Bonne semaine tout le monde !"
              className="w-full h-32 bg-slate-800 border border-slate-700 text-white rounded-lg p-3 resize-none focus:outline-none focus:border-amber-500"
              data-testid="broadcast-input"
            />
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" className="border-slate-700" onClick={() => setShowBroadcastModal(false)} disabled={sending}>
                Annuler
              </Button>
              <Button
                className="btn-primary-glow"
                onClick={handleBroadcast}
                disabled={!broadcastText.trim() || sending}
                data-testid="btn-send-broadcast"
              >
                {sending ? 'Envoi...' : 'Envoyer a tous'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ SUB-COMPONENTS ============

const ChatMessages = ({ messages, userId, loading, messagesEndRef, formatTime }) => {
  if (loading && messages.length === 0) {
    return (
      <div className="flex-1 p-4 space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
            <div className="w-2/3 h-16 bg-slate-800 rounded-xl animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-slate-500 p-6">
        <div className="text-center">
          <MessageCircle className="mx-auto mb-3 text-slate-700" size={48} />
          <p>Aucun message pour l'instant</p>
          <p className="text-sm text-slate-600 mt-1">Ecrivez le premier ci-dessous</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="chat-messages">
      {messages.map((msg, idx) => {
        const isOwn = msg.sender_id === userId;
        const showDate = idx === 0 || (
          new Date(messages[idx - 1].created_at).toDateString() !==
          new Date(msg.created_at).toDateString()
        );
        return (
          <React.Fragment key={msg.id}>
            {showDate && (
              <div className="text-center my-3">
                <span className="text-xs text-slate-500 bg-slate-900 px-3 py-1 rounded-full">
                  {new Date(msg.created_at).toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long' })}
                </span>
              </div>
            )}
            <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${msg.id}`}>
              <div
                className={`max-w-[75%] rounded-2xl px-4 py-2.5 shadow-sm ${isOwn
                  ? 'bg-amber-500 text-black rounded-br-sm'
                  : 'bg-slate-800 text-white rounded-bl-sm'
                  } ${msg._pending ? 'opacity-60' : ''}`}
              >
                {!isOwn && msg.broadcast_scope === 'all_drivers' && (
                  <p className="text-xs text-amber-400 font-semibold mb-1 flex items-center gap-1">
                    <Megaphone size={12} /> Annonce
                  </p>
                )}
                <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : ''}`}>
                  <span className={`text-xs ${isOwn ? 'text-black/60' : 'text-slate-500'}`}>
                    {formatTime(msg.created_at)}
                  </span>
                  {isOwn && !msg._pending && (
                    (msg.read_by || []).length > 0 || msg.read
                      ? <CheckCheck size={14} className="text-black/70" />
                      : <Check size={14} className="text-black/50" />
                  )}
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
};

const MessageInput = ({ newMessage, setNewMessage, onSend, sending, isRecording, toggleRecording }) => {
  return (
    <div className="flex-shrink-0 bg-slate-900 border-t border-slate-800 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]" data-testid="msg-input-bar">
      <form onSubmit={onSend} className="flex items-center gap-2">
        <button
          type="button"
          onClick={toggleRecording}
          className={`h-11 w-11 rounded-full flex items-center justify-center flex-shrink-0 transition ${isRecording ? 'bg-red-500 text-white animate-pulse' : 'bg-slate-800 text-amber-500 hover:bg-slate-700'}`}
          title={isRecording ? 'Arreter la dictee' : 'Dicter un message (mains libres)'}
          data-testid="btn-toggle-mic"
        >
          {isRecording ? <MicOff size={20} /> : <Mic size={20} />}
        </button>
        <Input
          value={newMessage}
          onChange={e => setNewMessage(e.target.value)}
          placeholder={isRecording ? 'Ecoute... parlez maintenant' : 'Ecrire un message...'}
          className="flex-1 h-11 bg-slate-800 border-slate-700 text-white"
          disabled={sending}
          data-testid="input-message"
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(e); } }}
        />
        <Button
          type="submit"
          className="h-11 w-11 btn-primary-glow p-0 flex-shrink-0"
          disabled={!newMessage.trim() || sending}
          data-testid="btn-send-message"
        >
          {sending ? (
            <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
          ) : (
            <Send size={18} />
          )}
        </Button>
      </form>
    </div>
  );
};

export default MessagesPage;
